/**
 * Estado da frota ao vivo.
 *
 * O `WS /stream` manda o snapshot completo na conexão e depois só quem mudou —
 * então aqui o mapa é por IMEI e cada delta sobrescreve a entrada.
 */
import { create } from "zustand";
import type { Vehicle } from "../lib/schemas.ts";
import type { VehicleStatus } from "../lib/fleet.ts";
import { appendTrailPoint, toTrailPoint, type TrailPoint } from "../lib/trail.ts";

export type ConnectionState = "idle" | "connecting" | "live" | "reconnecting" | "offline";

interface FleetState {
  vehicles: Map<string, Vehicle>;
  /**
   * Rastro por IMEI, alimentado ao vivo.
   *
   * Acumula para TODOS os veículos, não só o selecionado: quem estava
   * acompanhando um ônibus, foi olhar outro e voltou, encontra o rastro onde
   * deixou. O custo é limitado por desenho — 10 pontos por equipamento.
   */
  trails: Map<string, readonly TrailPoint[]>;
  updatedAt: string | null;
  /** IMEIs que mudaram na última mensagem — o realce some sozinho. */
  recentlyChanged: Set<string>;
  connection: ConnectionState;
  warning: string | null;
  selectedImei: string | null;
  /**
   * A gaveta de detalhe está aberta?
   *
   * Separado de `selectedImei` de propósito: fechar a gaveta **não** tira a
   * seleção. O veículo segue destacado no mapa, com o rastro desenhado, e quem
   * fechou só quis o mapa inteiro de volta. Para largar o veículo existe o
   * botão de desselecionar, que só aparece quando há algo selecionado.
   */
  detailOpen: boolean;
  search: string;
  statusFilter: VehicleStatus | null;

  applySnapshot: (vehicles: Vehicle[], generatedAt: string) => void;
  applyPositions: (vehicles: Vehicle[], at: string) => void;
  setConnection: (connection: ConnectionState) => void;
  setWarning: (warning: string | null) => void;
  select: (imei: string | null) => void;
  /** Fecha a gaveta mantendo a seleção. */
  closeDetail: () => void;
  setSearch: (search: string) => void;
  setStatusFilter: (status: VehicleStatus | null) => void;
  reset: () => void;
}

export const useFleetStore = create<FleetState>((set) => ({
  vehicles: new Map(),
  trails: new Map(),
  updatedAt: null,
  recentlyChanged: new Set(),
  connection: "idle",
  warning: null,
  selectedImei: null,
  detailOpen: false,
  search: "",
  statusFilter: null,

  applySnapshot: (vehicles, generatedAt) =>
    set((state) => {
      // O snapshot chega na conexão E em toda RECONEXÃO. Zerar o rastro aqui
      // apagaria o trajeto a cada oscilação de rede — justamente quando o
      // operador quer saber por onde o veículo andou. Ele é preservado e só
      // ganha o ponto atual.
      const trails = new Map(state.trails);
      for (const vehicle of vehicles) {
        const point = toTrailPoint(vehicle);
        if (point) trails.set(vehicle.imei, appendTrailPoint(trails.get(vehicle.imei) ?? [], point));
      }
      return {
        vehicles: new Map(vehicles.map((vehicle) => [vehicle.imei, vehicle])),
        trails,
        updatedAt: generatedAt,
        recentlyChanged: new Set(),
        warning: null,
      };
    }),

  applyPositions: (vehicles, at) =>
    set((state) => {
      // Mapa novo, e não mutação: o React compara por referência, e mutar o
      // anterior faria a tela não redesenhar — com o dado certo por dentro.
      const next = new Map(state.vehicles);
      // `trails` só vira mapa novo se algum ponto realmente entrou: o
      // `appendTrailPoint` devolve o array anterior quando o veículo não andou o
      // suficiente, e assim o mapa segue idêntico e o mapa não redesenha.
      let trails = state.trails;
      for (const vehicle of vehicles) {
        next.set(vehicle.imei, vehicle);
        const point = toTrailPoint(vehicle);
        if (!point) continue;
        const current = trails.get(vehicle.imei) ?? [];
        const updated = appendTrailPoint(current, point);
        if (updated !== current) {
          if (trails === state.trails) trails = new Map(state.trails);
          trails.set(vehicle.imei, updated);
        }
      }
      return {
        vehicles: next,
        trails,
        updatedAt: at,
        recentlyChanged: new Set(vehicles.map((vehicle) => vehicle.imei)),
      };
    }),

  setConnection: (connection) => set({ connection }),
  setWarning: (warning) => set({ warning }),
  // Escolher um veículo reabre a gaveta: é o caminho de volta depois de alguém
  // ter fechado e continuado com o veículo selecionado.
  select: (selectedImei) => set({ selectedImei, detailOpen: selectedImei !== null }),
  closeDetail: () => set({ detailOpen: false }),
  setSearch: (search) => set({ search }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  reset: () =>
    set({
      vehicles: new Map(),
      trails: new Map(),
      updatedAt: null,
      recentlyChanged: new Set(),
      connection: "idle",
      detailOpen: false,
    }),
}));
